import axios, { AxiosError } from 'axios'
import type { 
  AddressResult, 
  RouteStatsResponse, 
  CreateOrderRequest,
  CreateOrderResponse,
  OrderProgress,
  CurrencyRate,
} from '../types'

const api = axios.create({
  baseURL: '/api/v1',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Retry configuration
const MAX_RETRIES = 3
const RETRY_DELAY = 1000

// Helper for retry logic with exponential backoff
const withRetry = async <T>(
  fn: () => Promise<T>,
  retries = MAX_RETRIES,
  delay = RETRY_DELAY
): Promise<T> => {
  try {
    return await fn()
  } catch (error) {
    if (retries === 0) throw error
    
    const axiosError = error as AxiosError
    // Only retry on network errors or 5xx
    if (axiosError.response && axiosError.response.status < 500) {
      throw error
    }
    
    console.log(`Retrying... ${MAX_RETRIES - retries + 1}/${MAX_RETRIES}`)
    await new Promise(resolve => setTimeout(resolve, delay))
    return withRetry(fn, retries - 1, delay * 2)
  }
}

// Geocoding API
export const searchAddress = async (
  query: string, 
  limit = 5
): Promise<AddressResult[]> => {
  const { data } = await api.get<AddressResult[]>('/geo/search', {
    params: { query, limit, lang: 'ru' },
  })
  return data
}

export const reverseGeocode = async (
  lat: number, 
  lon: number
): Promise<AddressResult> => {
  const { data } = await api.get<AddressResult>('/geo/reverse', {
    params: { lat, lon, lang: 'ru' },
  })
  return data
}

// Taxi API
export const getRouteStats = async (
  route: [number, number][],
  userId?: string
): Promise<RouteStatsResponse> => {
  // Validate route before sending
  if (!route || route.length < 2) {
    throw new Error('Route must have at least 2 points')
  }
  
  // Validate coordinates
  for (const point of route) {
    if (!Array.isArray(point) || point.length !== 2) {
      throw new Error('Each route point must be [lon, lat]')
    }
    const [lon, lat] = point
    if (typeof lon !== 'number' || typeof lat !== 'number') {
      throw new Error('Coordinates must be numbers')
    }
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) {
      throw new Error('Invalid coordinates')
    }
  }
  
  // Use retry for better reliability
  return withRetry(async () => {
    const { data } = await api.post<RouteStatsResponse>('/taxi/routestats', {
      route,
      user_id: userId,
      use_toll_roads: false,
    })
    
    // Ensure price_usdt has fallback values
    if (data.service_levels) {
      data.service_levels = data.service_levels.map(level => ({
        ...level,
        price_usdt: level.price_usdt ?? level.price_rub * 0.011, // Fallback rate
      }))
    }
    
    return data
  })
}

// Генерация UUID совместимая со всеми браузерами
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  // Fallback для старых браузеров
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

export const createOrder = async (
  request: CreateOrderRequest
): Promise<CreateOrderResponse> => {
  console.log('Creating order with request:', request)
  try {
    const { data } = await api.post<CreateOrderResponse>('/taxi/orders', request, {
      headers: {
        'X-Idempotency-Token': generateUUID(),
      },
    })
    console.log('Order created successfully:', data)
    return data
  } catch (error) {
    console.error('Order creation failed:', error)
    throw error
  }
}

export const getOrderProgress = async (
  orderId: string
): Promise<OrderProgress> => {
  const { data } = await api.get<OrderProgress>(`/taxi/orders/${orderId}/progress`)
  return data
}

export const getOrderHistory = async (
  userId: string,
  limit = 50,
  offset = 0
): Promise<import('../types').OrderHistoryResponse> => {
  const { data } = await api.get('/taxi/orders/history', {
    params: { user_id: userId, limit, offset },
  })
  return data
}

export const submitFeedback = async (
  orderId: string,
  rating: number,
  msg?: string
): Promise<{ success: boolean; message?: string }> => {
  const { data } = await api.post(`/taxi/orders/${orderId}/feedback`, {
    rating,
    msg: msg || undefined,
  })
  return data
}

export const cancelOrder = async (
  orderId: string,
  cancelState = 'free'
): Promise<{ status: string; message?: string }> => {
  const { data } = await api.post(`/taxi/orders/${orderId}/cancel`, {
    cancel_state: cancelState,
  })
  return data
}

// Currency API
export const getCurrencyRate = async (): Promise<CurrencyRate> => {
  const { data } = await api.get<CurrencyRate>('/taxi/currency/rate')
  return data
}

// Payment Integration API (Prime Wallet)
export interface PaymentReserveRequest {
  user_id: number
  amount_rub: number
  merchant_name?: string
  merchant_photo?: string
}

export interface PaymentReserveResponse {
  id: number
  user_id: number
  reserve_id: string
  status_id: number
  amount_usd: string
  amount_rub: string
  rub_to_usd_fixed_rate: string
  merchant_name: string
  merchant_photo: string
  created_at: string
  updated_at: string
  expires_at: string
  ofd_check?: string
}

export const createPaymentReserve = async (
  request: PaymentReserveRequest
): Promise<PaymentReserveResponse> => {
  const { data } = await api.post<PaymentReserveResponse>('/taxi/payment/create-reserve', request)
  return data
}

export const approvePaymentReserve = async (
  reserveId: string
): Promise<PaymentReserveResponse> => {
  const { data } = await api.post<PaymentReserveResponse>(
    '/taxi/payment/approve-reserve',
    null,
    { params: { reserve_id: reserveId } }
  )
  return data
}

export const cancelPaymentReserve = async (
  reserveId: string
): Promise<PaymentReserveResponse> => {
  const { data } = await api.post<PaymentReserveResponse>(
    '/taxi/payment/cancel-reserve',
    null,
    { params: { reserve_id: reserveId } }
  )
  return data
}

export default api
